import { Injectable } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { defer, Observable, TimeoutError } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { ApplicationInsightsService, HttpDependencyOutcome, RequestTelemetry } from './application-insights.service';

const REQUEST_ORIGIN = new HttpContextToken<RequestTelemetry | null>(() => null);

/**
 * Captures each Angular request attempt and propagates W3C context to RecoveryTrac services.
 */
@Injectable()
export class TraceparentInterceptor implements HttpInterceptor {
  constructor(
    private applicationInsightsService: ApplicationInsightsService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.applicationInsightsService.isStaticRequest(req.url)) {
      return next.handle(req);
    }
    const origin = req.context.get(REQUEST_ORIGIN) || this.applicationInsightsService.beginRequest(req.method, req.url);
    req.context.set(REQUEST_ORIGIN, origin);
    return defer(() => {
      const attempt = this.applicationInsightsService.beginRequest(req.method, req.url, origin.snapshot, origin);
      const request = req.clone({ setHeaders: this.applicationInsightsService.correlationHeaders(req.url, attempt) });
      let outcome: HttpDependencyOutcome = { type: 'cancelled' };
      return new Observable<HttpEvent<unknown>>(subscriber =>
        this.applicationInsightsService.instrumentRequest(attempt, () => next.handle(request).subscribe(subscriber))
      ).pipe(
        tap({
          next: event => { if (event instanceof HttpResponse) { outcome = { type: 'http', status: event.status }; } },
          error: error => {
            if (error instanceof TimeoutError) { outcome = { type: 'timeout' }; }
            else if (error instanceof HttpErrorResponse && error.status) { outcome = { type: 'http', status: error.status }; }
            else { outcome = { type: 'network-error' }; }
          },
          complete: () => { if (outcome.type === 'cancelled') { outcome = { type: 'network-error' }; } },
        }),
        finalize(() => this.applicationInsightsService.completeRequest(attempt, outcome))
      );
    });
  }
}
